// packages/chatbot/src/components/ToolRenderer.tsx
import { Card, Button } from '@dopomogai/ui';
import { AlertTriangle, Wrench } from 'lucide-react';
import type { ToolRegistry } from '../types';

interface ToolRendererProps {
  toolName: string;
  args: any;
  toolRegistry: ToolRegistry;
  onSubmit: (result: any) => void;
  onCancel: () => void;
}

export function ToolRenderer({ toolName, args, toolRegistry, onSubmit, onCancel }: ToolRendererProps) {
  const ToolComponent = toolRegistry[toolName];

  // Special-case: frontend_actions is intended to be headless. If it
  // reaches here, we present a soft, minimal notice instead of an alarming error.
  if (!ToolComponent && toolName === 'frontend_actions') {
    return (
      <Card className="p-3 bg-sky-50 border-2 border-sky-300">
        <div className="flex items-start gap-3 text-sky-900">
          <Wrench className="w-5 h-5 mt-1 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-semibold mb-1">Action planned</div>
            <p className="mb-2">
              This step requires page integration and usually runs automatically. No input is needed here.
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" text="Dismiss" onClick={onCancel} />
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!ToolComponent) {
    // Generic, friendlier fallback for truly unregistered tools
    return (
      <Card className="p-4 bg-amber-50 border-2 border-amber-300">
        <div className="flex items-start gap-3 text-amber-900">
          <AlertTriangle className="w-5 h-5 mt-1 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-bold mb-2">This action isn’t available</div>
            <p className="mb-3">
              The agent requested the tool <code className="bg-amber-200 px-1 py-0.5 rounded">{toolName}</code>, but this widget
              doesn’t provide a UI for it.
            </p>
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-800">View arguments</summary>
              <pre className="bg-amber-100 p-2 rounded overflow-x-auto max-h-40 mt-1">
                {JSON.stringify(args, null, 2)}
              </pre>
            </details>
            <div className="mt-3">
              <Button variant="secondary" size="sm" text="Dismiss" onClick={onCancel} />
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Render registered interactive tool
  return (
    <Card className="p-4 bg-white border-2 border-emerald-400 shadow-lg">
      <ToolComponent args={args} onSubmit={onSubmit} onCancel={onCancel} />
    </Card>
  );
}
